import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Tooltip,
  Divider,
} from '@mui/material';
import { Delete as DeleteIcon, CalendarToday as CalendarIcon, DeleteSweep as DeleteSweepIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchTasks, deleteTask } from '../store/taskSlice';
import type { Task } from '../api/api';
import LoadingSpinner from './LoadingSpinner';

type DeleteMode = 'single' | 'day' | 'all';

interface DeleteState {
  mode: DeleteMode;
  taskId?: number;
  dateKey?: string;
  taskIds?: number[];
}

const formatTime = (isoString: string) => {
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const formatDateKey = (isoString: string) => {
  const date = new Date(isoString);
  return date.toISOString().split('T')[0]; // YYYY-MM-DD format for grouping
};

const formatDateHeader = (dateKey: string) => {
  const date = new Date(dateKey);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const isToday = date.toDateString() === today.toDateString();
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) {
    return 'Today';
  }
  if (isYesterday) {
    return 'Yesterday';
  }

  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const calculateDuration = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours === 0 && minutes === 0) {
    const seconds = Math.floor(diffMs / 1000);
    return `${seconds}s`;
  }

  if (hours === 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
};

const CompletedTasks: React.FC = () => {
  const dispatch = useAppDispatch();
  const tasks = useAppSelector(state => state.tasks.tasks);
  const loading = useAppSelector(state => state.tasks.loading);
  const [deleteState, setDeleteState] = useState<DeleteState | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(fetchTasks());
  }, [dispatch]);

  const handleDeleteSingle = (id: number) => {
    setDeleteState({ mode: 'single', taskId: id });
  };

  const handleDeleteDay = (dateKey: string, taskIds: number[]) => {
    setDeleteState({ mode: 'day', dateKey, taskIds });
  };

  const handleDeleteAll = (taskIds: number[]) => {
    setDeleteState({ mode: 'all', taskIds });
  };

  const handleConfirmDelete = async () => {
    if (!deleteState) return;
    
    setIsDeleting(true);
    try {
      if (deleteState.mode === 'single' && deleteState.taskId !== undefined) {
        await dispatch(deleteTask(deleteState.taskId)).unwrap();
      } else if (deleteState.taskIds && deleteState.taskIds.length > 0) {
        // Delete multiple tasks sequentially
        for (const taskId of deleteState.taskIds) {
          await dispatch(deleteTask(taskId)).unwrap();
        }
      }
      setDeleteState(null);
    } catch (error) {
      // Error deleting task(s)
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteState(null);
  };

  const getDeleteDialogContent = () => {
    if (!deleteState) return { title: '', message: '' };
    
    switch (deleteState.mode) {
      case 'single':
        return {
          title: 'Delete Task',
          message: 'Are you sure you want to delete this task? This action cannot be undone.'
        };
      case 'day':
        return {
          title: 'Delete All Tasks for This Day',
          message: `Are you sure you want to delete all ${deleteState.taskIds?.length || 0} tasks from ${deleteState.dateKey ? formatDateHeader(deleteState.dateKey) : 'this day'}? This action cannot be undone.`
        };
      case 'all':
        return {
          title: 'Delete All Completed Tasks',
          message: `Are you sure you want to delete all ${deleteState.taskIds?.length || 0} completed tasks? This action cannot be undone.`
        };
    }
  };

  // Filter only completed tasks: must have endDate set and not have isPaused flag
  // Sort from newest to oldest (descending by startDate)
  const completedTasks = useMemo(() => {
    return tasks
      .filter((task: Task) => task.endDate !== null && (task.isPaused === null || task.isPaused === undefined))
      .sort((a, b) => {
        if (!a.startDate || !b.startDate) return 0;
        return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
      });
  }, [tasks]);

  // Group tasks by day (based on startDate)
  const groupedTasks = useMemo(() => {
    const groups: Record<string, Task[]> = {};
    
    completedTasks.forEach((task: Task) => {
      if (task.startDate) {
        const dateKey = formatDateKey(task.startDate);
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(task);
      }
    });

    // Sort groups by date (newest first)
    const sortedGroupKeys = Object.keys(groups).sort((a, b) => 
      new Date(b).getTime() - new Date(a).getTime()
    );

    return sortedGroupKeys.map(key => ({
      date: key,
      tasks: groups[key]
    }));
  }, [completedTasks]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ px: { xs: 0, sm: 3 } }}>
        <Box sx={{ my: { xs: 0, sm: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <LoadingSpinner />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ my: { xs: 0, sm: 4 } }}>
        <Typography variant="h4" component="h1" sx={{ textAlign: 'center', pt: { xs: 1, sm: 0 }, mb: { xs: 1, sm: 2 }, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Completed Tasks
        </Typography>
        {completedTasks.length === 0 ? (
          <Paper sx={{ p: { xs: 1, sm: 3 }, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No completed tasks yet. Complete a task to see it here!
            </Typography>
          </Paper>
        ) : (
          <Box>
            {/* Delete All Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={() => handleDeleteAll(completedTasks.map(t => t.id))}
                size="small"
              >
                Delete All ({completedTasks.length})
              </Button>
            </Box>

            {groupedTasks.map((group, groupIndex) => (
              <Box key={group.date} sx={{ mb: { xs: 2, sm: 3 } }}>
                {/* Day Header */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 1.5,
                    mt: groupIndex > 0 ? 2 : 0
                  }}
                >
                  <CalendarIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      fontSize: { xs: '1rem', sm: '1.1rem' }
                    }}
                  >
                    {formatDateHeader(group.date)}
                  </Typography>
                  <Divider sx={{ flexGrow: 1, ml: 1 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontWeight: 500,
                      px: 1
                    }}
                  >
                    {group.tasks.length} {group.tasks.length === 1 ? 'task' : 'tasks'}
                  </Typography>
                  <Tooltip title={`Delete all tasks from ${formatDateHeader(group.date)}`} placement="top">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteDay(group.date, group.tasks.map(t => t.id))}
                      sx={{ ml: 0.5 }}
                    >
                      <DeleteSweepIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Tasks for this day */}
                <List sx={{ pt: 0 }}>
                  {group.tasks.map((task: Task) => (
                    <Paper key={task.id} sx={{ mb: { xs: 1, sm: 1.5 } }}>
                      <ListItem
                        sx={{
                          flexWrap: { xs: 'wrap', sm: 'nowrap' },
                          gap: { xs: 1, sm: 0 }
                        }}
                        secondaryAction={
                          <Tooltip title="Click to delete this task" placement="top">
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => handleDeleteSingle(task.id)}
                              color="error"
                              size="medium"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <ListItemText
                          primary={
                            <Typography variant="h6">{task.name}</Typography>
                          }
                          secondary={
                            <Box>
                              <Typography component="span" display="block">
                                Category: {task.category}
                              </Typography>
                              <Typography component="span" display="block">
                                Subcategory: {task.subcategory}
                              </Typography>
                              {task.startDate && task.endDate && (
                                <Typography component="span" display="block">
                                  Time: {formatTime(task.startDate)} - {formatTime(task.endDate)}
                                </Typography>
                              )}
                              {task.startDate && task.endDate && (
                                <Typography component="span" display="block" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                  Duration: {calculateDuration(task.startDate, task.endDate)}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </Paper>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        )}

        <Dialog
          open={deleteState !== null}
          onClose={handleCancelDelete}
          aria-labelledby="delete-task-dialog"
        >
          <DialogTitle id="delete-task-dialog">{getDeleteDialogContent().title}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {getDeleteDialogContent().message}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary" disabled={isDeleting}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete} 
              color="error" 
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CompletedTasks;
